import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CarsTable from '../expensesComponents/CarsTable';
import ViewSetting from '../modal/ViewSetting';
import SettingsBlock from '../util/SettingsBlock';

interface ExpensesSettingsProps {
  access: any;
  baseUrl: string;
  unit: any;
  refreshFields: any;
}

const LBL: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '.06em',
  color: 'var(--text-color-calendar-week)',
  marginBottom: 4,
};

const INP: React.CSSProperties = {
  width: 90,
  height: 32,
  border: 'var(--border-input)',
  borderRadius: 'var(--radius-input)',
  background: 'var(--bg-input)',
  color: 'var(--text-color)',
  fontSize: 13,
  padding: '0 8px',
  outline: 'none',
  fontFamily: 'inherit',
};

const GeneralTab: React.FC = () => {
  const [expenseDays,  setExpenseDays]  = useState<string>('');
  const [carDays,      setCarDays]      = useState<string>('');
  const [rate1Label,   setRate1Label]   = useState<string>('');
  const [rate1Price,   setRate1Price]   = useState<string>('');
  const [rate2Label,   setRate2Label]   = useState<string>('');
  const [rate2Price,   setRate2Price]   = useState<string>('');
  const [saving,       setSaving]       = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get('user');
      const c = data?.company;
      setExpenseDays(c?.finance_expense_claim_days != null ? String(c.finance_expense_claim_days) : '');
      setCarDays(c?.finance_car_claim_days != null ? String(c.finance_car_claim_days) : '');
      setRate1Label(c?.car_rate1_label ?? '');
      setRate1Price(c?.car_rate1_price != null ? String(c.car_rate1_price) : '');
      setRate2Label(c?.car_rate2_label ?? '');
      setRate2Price(c?.car_rate2_price != null ? String(c.car_rate2_price) : '');
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put('company', {
        finance_expense_claim_days: expenseDays.trim() === '' ? null : Number(expenseDays),
        finance_car_claim_days:     carDays.trim()    === '' ? null : Number(carDays),
        car_rate1_label: rate1Label.trim() || null,
        car_rate1_price: rate1Price.trim() === '' ? null : Number(rate1Price),
        car_rate2_label: rate2Label.trim() || null,
        car_rate2_price: rate2Price.trim() === '' ? null : Number(rate2Price),
      });
    } catch {} finally {
      setSaving(false);
    }
  };

  const INP_PRICE: React.CSSProperties = { ...INP, width: 80 };
  const INP_LABEL: React.CSSProperties = { ...INP, width: 160 };

  return (
    <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={LBL}>{t('ExpensesClaimDays') || 'Expenses — No Later Than'}</div>
        <input type="number" min={1} max={365} value={expenseDays}
          onChange={e => setExpenseDays(e.target.value)} placeholder="—" style={INP} />
      </div>
      <div>
        <div style={LBL}>{t('CarExpensesClaimDays') || 'Car Expenses — No Later Than'}</div>
        <input type="number" min={1} max={365} value={carDays}
          onChange={e => setCarDays(e.target.value)} placeholder="—" style={INP} />
      </div>

      {/* Mileage rates */}
      <div style={{ borderTop: '1px solid var(--border2-new)', paddingTop: 14 }}>
        <div style={{ ...LBL, marginBottom: 10 }}>{t('MileageRates') || 'Mileage Rates'}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input value={rate1Label} onChange={e => setRate1Label(e.target.value)}
              placeholder={t('Rate1Name') || 'Rate 1 name'} style={INP_LABEL} />
            <input type="number" min={0} step={0.01} value={rate1Price}
              onChange={e => setRate1Price(e.target.value)}
              placeholder="0.00" style={INP_PRICE} />
            <span style={{ fontSize: 12, color: 'var(--text-color-calendar-week)' }}>/ unit</span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input value={rate2Label} onChange={e => setRate2Label(e.target.value)}
              placeholder={t('Rate2Name') || 'Rate 2 name'} style={INP_LABEL} />
            <input type="number" min={0} step={0.01} value={rate2Price}
              onChange={e => setRate2Price(e.target.value)}
              placeholder="0.00" style={INP_PRICE} />
            <span style={{ fontSize: 12, color: 'var(--text-color-calendar-week)' }}>/ unit</span>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="outlined" color="success" onClick={handleSave} disabled={saving}
          sx={{ fontSize: 13, height: 32, minWidth: 140 }}>
          {saving ? <CircularProgress size={16} color="inherit" /> : t('Save Change')}
        </Button>
      </div>
    </div>
  );
};

const ExpensesSettings: React.FC<ExpensesSettingsProps> = ({ access, baseUrl, unit, refreshFields }) => (
  <div className="text-[var(--text-color)]">
    <SettingsBlock title={t('General') || 'General'}>
      <GeneralTab />
    </SettingsBlock>
    <SettingsBlock title={t('CompanyVehicles') || 'Company Vehicles'} defaultOpen={false}>
      <CarsTable baseUrl={baseUrl} unit={unit} />
    </SettingsBlock>
    {!access?.expenses_user && (
      <SettingsBlock title={t('ExpenseFields') || 'Expense Fields'} defaultOpen={false}>
        <ViewSetting refreshFields={refreshFields} />
      </SettingsBlock>
    )}
  </div>
);

export default ExpensesSettings;
